import type { Metric } from 'web-vitals';

/**
 * Đo Core Web Vitals trên trình duyệt rồi đưa ra nơi xem được.
 * Dev: console. Production: POST /api/client-vitals (Pino), chỉ khi chậm.
 * ObjectId trên URL được thay bằng `:id` trước khi gửi.
 */

export type VitalPayload = {
  name: Metric['name'];
  value: number;
  rating: Metric['rating'];
  navigationType: Metric['navigationType'];
  path: string;
};

export function vitalPath(pathname: string): string {
  const path = pathname.split('?')[0]?.split('#')[0] ?? '/';
  return path
    .split('/')
    .map((segment) => (/^[a-f0-9]{24}$/i.test(segment) ? ':id' : segment))
    .join('/')
    .slice(0, 160);
}

export function vitalPayload(
  metric: Pick<Metric, 'name' | 'value' | 'rating' | 'navigationType'>,
  pathname: string,
): VitalPayload {
  const value = metric.name === 'CLS'
    ? Math.round(metric.value * 1000) / 1000
    : Math.round(metric.value);
  return {
    name: metric.name,
    value,
    rating: metric.rating,
    navigationType: metric.navigationType,
    path: vitalPath(pathname),
  };
}

function vitalsEndpoint(): string {
  const raw = (import.meta.env.VITE_API_URL || 'http://localhost:5001').replace(/\/+$/, '');
  const base = raw.endsWith('/api') ? raw : `${raw}/api`;
  return `${base}/client-vitals`;
}

function sendVital(metric: Metric): void {
  const payload = vitalPayload(metric, window.location.pathname);
  if (import.meta.env.DEV) {
    console.info('[web-vitals]', payload);
    return;
  }
  if (payload.rating === 'good') return;

  const body = JSON.stringify(payload);
  const url = vitalsEndpoint();
  if (typeof navigator.sendBeacon === 'function') {
    const blob = new Blob([body], { type: 'application/json' });
    if (navigator.sendBeacon(url, blob)) return;
  }
  void fetch(url, {
    method: 'POST',
    body,
    keepalive: true,
    headers: { 'Content-Type': 'application/json' },
  });
}

const reportWebVitals = (): void => {
  void import('web-vitals').then(({ onCLS, onFCP, onINP, onLCP, onTTFB }) => {
    onCLS(sendVital);
    onFCP(sendVital);
    onINP(sendVital);
    onLCP(sendVital);
    onTTFB(sendVital);
  });
};

export default reportWebVitals;
